import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogConnectNewSmartlockAccountComponent } from '@app/modules/apps/components/dialog-connect-new-smartlock-account/dialog-connect-new-smartlock-account.component';
import { DialogEditSmartlockComponent } from '@app/modules/apps/components/dialog-edit-smartlock/dialog-edit-smartlock.component';
import { NotificationType } from '@app/shared/interfaces';
import {
  LockPayload,
  MatchedLock,
  SmartlockAccount,
  SmartlocksService,
  UnmatchedLock,
} from '@app/shared/services/smartlocks/smartlocks.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { IntegrationType, resolveIntegrationInfo } from '@app/shared/utils';
import { map } from 'rxjs/operators';
import { DialogResyncLocksComponent } from '../../components/dialog-resync-locks/dialog-resync-locks.component';
import { DialogSmartlockConnectionFailureComponent } from '../../components/dialog-smartlock-connection-failure/dialog-smartlock-connection-failure.component';

@Component({
  standalone: false,
  selector: 'sbnb-smartlock',
  templateUrl: './smartlock.component.html',
  styleUrls: ['./smartlock.component.scss'],
})
export class SmartlockComponent implements OnInit {
  isMobile$ = this.breakpointObserver.observe(['(max-width: 960px)']).pipe(map((res) => res.matches));

  learnMoreUrl = `https://my.hospitable.com/support-documentation/article/5757397`;
  learnMoreSmartThingsUrl = `https://my.hospitable.com/support-documentation/article/6714720-how-to-integrate-with-smartthings`;

  loadingLinkedLocks: boolean = true;
  loadingOtherLocks: boolean = true;
  loadingSmartlockAccounts: boolean = true;

  linkedLocks: MatchedLock[] = [];
  otherLocks: UnmatchedLock[] = [];
  smartlockAccounts: SmartlockAccount[] = [];
  accountDisconnected: boolean = false;

  saveStatus: number = 0;

  integration: IntegrationType | null = null;

  integrationId: string = '';

  loading: boolean = false;

  pageIdentifier: string;

  constructor(
    private smartlocksService: SmartlocksService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private toast: ToastNotificationsService,
    private breakpointObserver: BreakpointObserver
  ) { }

  ngOnInit() {
    const integrationData = resolveIntegrationInfo(this.router.url);

    if (integrationData) {
      this.integrationId = integrationData.integration_id;
      this.integration = integrationData.integration as IntegrationType;
      this.pageIdentifier = integrationData.page_identifier;

      this.getSmartlockAccounts();
    }

    this.route.paramMap.subscribe((params) => {
      const status = params.get('status');

      // popup the failure dialog if we failed to connect
      if (status === 'failure') {
        this.openConnectionFailureDialog();
      } else if (status === 'connection') {
        this.route.queryParams.subscribe((res) => {
          this.loading = true;
          const externalConnectionParam = res.code ?? res.connect_webview_id;

          this.finishAccountConnection(externalConnectionParam);
        });
      }
    });

    this.getLinkedLocks();
    this.getOtherLocks();
  }

  getLinkedLocks() {
    const payload: LockPayload = {
      integration_id: this.integrationId,
    };

    this.loadingLinkedLocks = true;

    this.smartlocksService.getLinkedLocks(payload).subscribe(
      (res: MatchedLock[]) => {
        this.linkedLocks = res;
        this.loadingLinkedLocks = false;

        this.linkedLocks.forEach((lock) => {
          this.fetchAdditionalInfoForLock(lock);
        });
      },
      (err) => {
        // TODO
        this.loadingLinkedLocks = false;
      }
    );
  }

  fetchAdditionalInfoForLock(lock: MatchedLock) {
    const payload = {
      integration_id: lock.integration_id,
      connection_id: lock.connection_id,
      lock_id: lock.lock_id,
    };

    this.smartlocksService.getAdditionalLockInfo(payload).subscribe((res) => {
      lock.additional_info = res;
    });
  }

  getOtherLocks() {
    const payload: LockPayload = {
      integration_id: this.integrationId,
    };

    this.loadingOtherLocks = true;

    this.smartlocksService.getOtherLocks(payload).subscribe(
      (res: UnmatchedLock[]) => {
        this.otherLocks = res;
        this.loadingOtherLocks = false;
      },
      (err) => {
        // TODO
        this.loadingOtherLocks = false;
      }
    );
  }

  getSmartlockAccounts() {
    const payload: any = {
      integration_id: this.integrationId,
    };
    this.smartlocksService.getSmartlockAccounts(payload).subscribe(
      (res: SmartlockAccount[]) => {
        this.smartlockAccounts = res;
        this.loadingSmartlockAccounts = false;

        this.accountDisconnected = this.smartlockAccounts.some((account) => {
          return account && account.disconnected;
        });

        if (this.smartlockAccounts.length === 0 && (this.linkedLocks.length > 0 || this.otherLocks.length > 0)) {
          this.accountDisconnected = true;
        }
      },
      (err) => {
        this.loadingSmartlockAccounts = false;
      }
    );
  }

  openConnectionFailureDialog() {
    const dialogRef = this.dialog.open(DialogSmartlockConnectionFailureComponent, {
      width: '720px',
      maxHeight: '90vh',
      data: {
        integration_id: this.integrationId,
      },
    });
  }

  learnMore() {
    if (this.integration === 'SmartThings') {
      window.open(this.learnMoreSmartThingsUrl, '_blank');
    } else {
      window.open(this.learnMoreUrl, '_blank');
    }
  }

  openConnectDialog(isReconnection = false) {
    const dialogRef = this.dialog.open(DialogConnectNewSmartlockAccountComponent, {
      width: '720px',
      maxHeight: '90vh',
      data: {
        integration_id: this.integrationId,
        integration_name: this.integration,
        isFirstConnection: this.linkedLocks.length > 0 || this.otherLocks.length > 0,
        isReconnection: isReconnection,
      },
    });
  }

  editLock(linkedLock: MatchedLock | UnmatchedLock) {
    const dialogRef = this.dialog.open(DialogEditSmartlockComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      disableClose: true,
      data: {
        lock: linkedLock,
        locks: [],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // The lock may have changed, refetch the locks
      this.getLinkedLocks();
      this.getOtherLocks();
    });
  }

  resyncLock(lock: MatchedLock) {
    const dialogRef = this.dialog.open(DialogResyncLocksComponent, {
      width: '720px',
      maxHeight: '90vh',
      data: {
        integration_id: this.integrationId,
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        lock.resyncing = true;

        const payload = {
          integration_id: lock.integration_id,
          connection_id: lock.connection_id,
          lock_id: lock.lock_id,
        };

        this.smartlocksService.initiateLockResync(payload).subscribe(
          (res: any) => {
            lock.resyncing = false;
          },
          (err) => {
            lock.resyncing = false;

            this.toast.open(err.message, 'Dismiss', NotificationType.Error);
          }
        );
      }
    });
  }

  finishAccountConnection(connectionCode: string) {
    this.loading = false;
    this.router.navigate([`/apps/${this.integration.toLowerCase()}/success`], {
      queryParams: {
        connectionParam: connectionCode,
      },
    });
  }
}
